import AllInclusiveRounded from '@mui/icons-material/AllInclusiveRounded';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import AutoGraphRounded from '@mui/icons-material/AutoGraphRounded';
import DashboardRounded from '@mui/icons-material/SpaceDashboardRounded';
import ExtensionRounded from '@mui/icons-material/ExtensionRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import HowToRegRounded from '@mui/icons-material/HowToRegRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import RedeemRounded from '@mui/icons-material/RedeemRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import { MODULES } from '@/modules/registry';
import type { NavItem } from '@/modules/types';
import { allows, isStaff } from '@/session/access';
import { useMe } from '@/session/useSession';
import { useTenant } from '@/tenant/useTenant';

export const TENANT_SETTINGS_PERMISSIONS = [
  'tenant.settings.manage',
  'tenant.members.read',
  'tenant.subscription.read',
  'tenant.audit.read',
];

const HOME: NavItem = { to: '/', label: 'Início', icon: DashboardRounded };

/** Everyone in a space: see what is included and turn on more modules. */
const MORE_MODULES: NavItem = { to: '/modulos', label: 'Módulos', icon: ExtensionRounded };

/** Students buy their own plan; professionals turn modules on in Módulos. */
const PLUS: NavItem = { to: '/plus', label: 'Plus', icon: WorkspacePremiumRounded };

/** Everyone: share a link and earn by Pix when the person starts paying. */
const REFERRALS: NavItem = { to: '/indique', label: 'Indique e ganhe', icon: RedeemRounded };

const SETTINGS: NavItem = {
  to: '/configuracoes',
  label: 'Configurações',
  icon: SettingsRounded,
  rule: { anyPermission: TENANT_SETTINGS_PERMISSIONS },
};

export const PLATFORM_NAV: NavItem[] = [
  { to: '/', label: 'Painel', icon: DashboardRounded },
  {
    to: '/tenants',
    label: 'Tenants',
    icon: ApartmentRounded,
    rule: { anyPermission: ['platform.tenants.read'] },
  },
  {
    to: '/planos',
    label: 'Planos',
    icon: SellRounded,
    rule: { anyPermission: ['platform.catalog.manage'] },
  },
  {
    to: '/vitalicios',
    label: 'Vitalícios',
    icon: AllInclusiveRounded,
    rule: { anyPermission: ['platform.tenants.manage'] },
  },
  {
    to: '/indicacoes',
    label: 'Indicações',
    icon: RedeemRounded,
    rule: { anyPermission: ['platform.tenants.read'] },
  },
  {
    to: '/interessados',
    label: 'Interessados',
    icon: HowToRegRounded,
    rule: { anyPermission: ['platform.tenants.read'] },
  },
  {
    to: '/visitas',
    label: 'Visitas',
    icon: InsightsRounded,
    rule: { anyPermission: ['platform.usage.read'] },
  },
  {
    to: '/usuarios',
    label: 'Usuários',
    icon: PeopleAltRounded,
    rule: { anyPermission: ['platform.users.read'] },
  },
  {
    to: '/consumo',
    label: 'Consumo',
    icon: AutoGraphRounded,
    rule: { anyPermission: ['platform.usage.read'] },
  },
  {
    to: '/ia',
    label: 'IA',
    icon: AutoAwesomeRounded,
    rule: { anyPermission: ['platform.usage.read', 'platform.ai.manage'] },
  },
  {
    to: '/auditoria',
    label: 'Auditoria',
    icon: HistoryRounded,
    rule: { anyPermission: ['platform.audit.read'] },
  },
];

/** Navigation for the current host and person: only what they can actually use. */
export function useNavItems(): NavItem[] {
  const me = useMe();
  const { isPlatform } = useTenant();
  const candidates = isPlatform
    ? PLATFORM_NAV
    : [
        HOME,
        ...MODULES.flatMap((module) => module.nav),
        MORE_MODULES,
        ...(isStaff(me) ? [] : [PLUS]),
        REFERRALS,
        SETTINGS,
      ];
  return candidates.filter((item) => allows(me, item.rule));
}

export function activeNavItem(items: NavItem[], pathname: string) {
  const match = [...items]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)));
  return match?.to ?? false;
}
